import { Component, Input } from '@angular/core';
import { resolveWidgetBarGraphConfig, WidgetBarGraphConfig, WidgetBarGraphType } from './widget-bar-graph.types';

/**
 * Generic, presentation-only bar graph. Does not read any Location View
 * models or Solution Participation business rules - all presentation config
 * is resolved from widgetType; callers only provide data.
 */
@Component({
    selector: 'app-widget-bar-graph',
    templateUrl: './widget-bar-graph.component.html',
    standalone: false
})
export class WidgetBarGraphComponent {

  @Input({ required: true }) widgetType!: WidgetBarGraphType;
  @Input({ required: true }) value!: number;
  @Input({ required: true }) total!: number;

  @Input() targetValue?: number;

  @Input() displayText = '';
  @Input() supportingText = '';
  /** Overrides the config's defaultAriaLabel when the caller needs dynamic text. */
  @Input() ariaLabel?: string;

  get config(): WidgetBarGraphConfig {
    return resolveWidgetBarGraphConfig(this.widgetType);
  }

  get resolvedAriaLabel(): string {
    return this.ariaLabel ?? this.config.defaultAriaLabel;
  }

  /** Filled percentage clamped to 0-100; invalid/zero/negative totals render as 0%. */
  get filledPercent(): number {
    if (!this.total || isNaN(this.total) || this.total <= 0 || isNaN(this.value)) {
      return 0;
    }
    return Math.min(100, Math.max(0, (this.value / this.total) * 100));
  }

  /** Target marker position clamped to 0-100; only meaningful when showTarget is true. */
  get targetPercent(): number {
    if (!this.total || isNaN(this.total) || this.total <= 0 || this.targetValue == null || isNaN(this.targetValue)) {
      return 0;
    }
    return Math.min(100, Math.max(0, (this.targetValue / this.total) * 100));
  }

  get showTarget(): boolean {
    return this.config.showTargetLine && this.targetValue != null && !isNaN(this.targetValue);
  }

  get displayTextSizeClass(): string {
    switch (this.config.size) {
      case 'large': return 'text-base';
      case 'medium': return 'text-sm';
      default: return 'text-xs';
    }
  }

  get currentLabel(): string {
    return isNaN(this.value) ? '0' : `${this.value}`;
  }

  get maxLabel(): string {
    return (!this.total || isNaN(this.total)) ? '0' : `${this.total}`;
  }
}
